/* ============================================================
   section-highlights-image-video.js
   ============================================================
   Custom element for the media tile of sections/highlights.liquid.
 
   Playback modes (data-playback):
     manual   – plays only via the play button
     hover    – plays on mouse enter (desktop) or tap (touch),
                pauses on mouse leave
     autoplay – starts muted in a loop as soon as it is visible
 
   Hover and autoplay playback is always muted, because browsers
   block unmuted playback that the user did not initiate. Sound
   is only enabled when the visitor clicks the play button and
   the section is not set to stay muted.
 
   Videos pause automatically once they scroll out of view.
   ============================================================ */
 
if (!customElements.get('highlight-media')) {
  customElements.define(
    'highlight-media',
    class HighlightMedia extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        if (!this.video) return;
 
        this.playBtn = this.querySelector('.hl__play');
        this.mode = this.dataset.playback || 'manual';
        this.keepMuted = this.dataset.keepMuted === 'true';
        this.showControls = this.dataset.showControls === 'true';
        this.rewindOnLeave = this.dataset.rewindOnLeave === 'true';
 
        this.canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
 
        // Required for inline playback on iOS.
        this.video.setAttribute('playsinline', '');
 
        this.video.addEventListener('play', this.onPlay);
        this.video.addEventListener('pause', this.onPause);
 
        if (this.playBtn) {
          this.playBtn.addEventListener('click', this.onPlayClick);
        }
 
        if (this.mode === 'hover') {
          if (this.canHover && !this.reducedMotion) {
            this.addEventListener('mouseenter', this.onEnter);
            this.addEventListener('mouseleave', this.onLeave);
          }
          // Touch devices (and anyone without hover) toggle by tapping.
          this.addEventListener('click', this.onTap);
        }
 
        this.observeVisibility();
      }
 
      disconnectedCallback() {
        this.video?.removeEventListener('play', this.onPlay);
        this.video?.removeEventListener('pause', this.onPause);
        this.playBtn?.removeEventListener('click', this.onPlayClick);
        this.removeEventListener('mouseenter', this.onEnter);
        this.removeEventListener('mouseleave', this.onLeave);
        this.removeEventListener('click', this.onTap);
        this.observer?.disconnect();
      }
 
      // ---------------------------------------------------------------
      // Playback
      // ---------------------------------------------------------------
 
      playMuted() {
        this.video.muted = true;
        const started = this.video.play();
        if (started && typeof started.catch === 'function') {
          started.catch(() => {});
        }
      }
 
      onPlayClick = (e) => {
        e.stopPropagation();
 
        if (!this.keepMuted) this.video.muted = false;
        this.video.controls = true;
 
        const started = this.video.play();
        if (started && typeof started.catch === 'function') {
          // Unmuted playback can still be rejected — fall back to muted.
          started.catch(() => this.playMuted());
        }
      };
 
      onEnter = () => {
        if (this.video.controls) return; // visitor took over via the button
        this.playMuted();
      };
 
      onLeave = () => {
        if (this.video.controls) return;
        this.video.pause();
        if (this.rewindOnLeave) this.video.currentTime = 0;
      };
 
      onTap = (e) => {
        if (e.target.closest('.hl__play')) return;
        if (this.canHover && !this.reducedMotion) return; // hover already handles it
 
        if (this.video.paused) {
          this.playMuted();
        } else {
          this.video.pause();
        }
      };
 
      // ---------------------------------------------------------------
      // Visibility
      // ---------------------------------------------------------------
 
      observeVisibility() {
        if (!('IntersectionObserver' in window)) {
          if (this.mode === 'autoplay') this.playMuted();
          return;
        }
 
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                if (this.mode === 'autoplay' && !this.reducedMotion) this.playMuted();
              } else if (!this.video.paused) {
                this.video.pause();
                if (this.rewindOnLeave && !this.video.controls) this.video.currentTime = 0;
              }
            });
          },
          { threshold: 0.25 }
        );
 
        this.observer.observe(this);
      }
 
      // ---------------------------------------------------------------
      // State
      // ---------------------------------------------------------------
 
      onPlay = () => {
        this.classList.add('is-playing');
      };
 
      onPause = () => {
        if (!this.showControls) this.classList.remove('is-playing');
      };
    }
  );
}